import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AutocompleteOptions,
  AutocompleteResponse,
  FederatedAutocompleteOptions,
  FederatedAutocompleteResponse,
  FederatedSearchOptions,
  FederatedSearchResponse,
  FederatedSuggestOptions,
  FederatedSuggestResponse,
  RelatedOptions,
  RelatedResponse,
  SearchOptions,
  SearchResponse,
  SuggestOptions,
  SuggestResponse,
  TrendingOptions,
  TrendingResponse,
  autocomplete as universalAutocomplete,
  federatedAutocomplete as universalFederatedAutocomplete,
  federatedSearch as universalFederatedSearch,
  federatedSuggest as universalFederatedSuggest,
  related as universalRelated,
  search as universalSearch,
  suggest as universalSuggest,
  trending as universalTrending,
} from './search-v1-sitedocument-site-search.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/search' };

export function search(httpClient: HttpClient): SearchSignature {
  return (options?: SearchOptions) =>
    universalSearch(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchSignature {
  /**
   * Executes a regular search query.
   * If you are unsure, this is likely the search method you want to used.
   */
  (options?: SearchOptions): Promise<
    NonNullablePaths<
      SearchResponse,
      | `documents`
      | `nextPage.total`
      | `nextPage.skip`
      | `nextPage.limit`
      | `facets`
      | `facets.${number}.terms.facet`
      | `facets.${number}.minAggregation.facet`
      | `facets.${number}.maxAggregation.facet`
      | `facets.${number}.minMaxAggregation.facet`
      | `facets.${number}.hierarchicalAggregation.facet`
      | `facets.${number}.sumAggregation.facet`,
      5
    >
  >;
}

export function federatedSearch(
  httpClient: HttpClient
): FederatedSearchSignature {
  return (options?: FederatedSearchOptions) =>
    universalFederatedSearch(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface FederatedSearchSignature {
  /**
   * Searches in multiple document types at once.
   */
  (options?: FederatedSearchOptions): Promise<
    NonNullablePaths<
      FederatedSearchResponse,
      `results` | `results.${number}.total`,
      4
    >
  >;
}

export function suggest(httpClient: HttpClient): SuggestSignature {
  return (options?: SuggestOptions) =>
    universalSuggest(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SuggestSignature {
  /**
   * Executes search query to fetch suggested items. Unlike search query suggest will match
   * partial phrases (for example "blu" will match documents containing "blue", "blues" and "blunt").
   * Phrase needs to be at least 3 symbols long. Suggestions can also perform optimisations in search
   * results and generally do not guarantee the same level of quality as regular Search endpoint.
   */
  (options?: SuggestOptions): Promise<
    NonNullablePaths<SuggestResponse, `documents`, 2>
  >;
}

export function federatedSuggest(
  httpClient: HttpClient
): FederatedSuggestSignature {
  return (options?: FederatedSuggestOptions) =>
    universalFederatedSuggest(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface FederatedSuggestSignature {
  /**
   * Searches for suggestions in multiple document types at once.
   */
  (options?: FederatedSuggestOptions): Promise<
    NonNullablePaths<FederatedSuggestResponse, `results`, 2>
  >;
}

export function related(httpClient: HttpClient): RelatedSignature {
  return (options?: RelatedOptions) =>
    universalRelated(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RelatedSignature {
  /**
   * Fetches documents similar to one single document.
   * This is typically used to implement "related to" scenarios (for example to fetch related store products when
   * consumer is already viewing one).
   */
  (options?: RelatedOptions): Promise<
    NonNullablePaths<RelatedResponse, `documents`, 2>
  >;
}

export function autocomplete(httpClient: HttpClient): AutocompleteSignature {
  return (options?: AutocompleteOptions) =>
    universalAutocomplete(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AutocompleteSignature {
  /**
   * Provides phrase completion. For example "blu" could return "blue", "blues" and "blunt" as candidate phrases. This operation is resource heavy at index time and is reserved for special use cases.
   */
  (options?: AutocompleteOptions): Promise<
    NonNullablePaths<
      AutocompleteResponse,
      `values` | `values.${number}.query`,
      4
    >
  >;
}

export function federatedAutocomplete(
  httpClient: HttpClient
): FederatedAutocompleteSignature {
  return (options?: FederatedAutocompleteOptions) =>
    universalFederatedAutocomplete(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface FederatedAutocompleteSignature {
  /**
   * Provides phrase completion from multiple document types at once
   */
  (options?: FederatedAutocompleteOptions): Promise<
    NonNullablePaths<FederatedAutocompleteResponse, `results`, 2>
  >;
}

export function trending(httpClient: HttpClient): TrendingSignature {
  return (options?: TrendingOptions) =>
    universalTrending(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TrendingSignature {
  /**
   * Returns trending documents for given document types
   */
  (options?: TrendingOptions): Promise<
    NonNullablePaths<
      TrendingResponse,
      `results` | `results.${number}.documentType`,
      4
    >
  >;
}

export {
  AccountInfo,
  Aggregation,
  AggregationFacet,
  AutocompleteOptions,
  AutocompleteRequest,
  AutocompleteResponse,
  AutocompleteResponseValue,
  DeleteByFilterOperation,
  DeleteByIdsOperation,
  Direction,
  DocumentImage,
  DocumentPayload,
  DocumentUpdateOperation,
  Empty,
  Enum,
  FacetClause,
  FacetClauseClauseOneOf,
  FacetClauses,
  FacetCountResponse,
  FacetsResponse,
  FacetsResponseResponseOneOf,
  FederatedAutocompleteOptions,
  FederatedAutocompleteRequest,
  FederatedAutocompleteResponse,
  FederatedAutocompleteResults,
  FederatedSearchDocuments,
  FederatedSearchOptions,
  FederatedSearchRequest,
  FederatedSearchResponse,
  FederatedSuggestDocuments,
  FederatedSuggestOptions,
  FederatedSuggestRequest,
  FederatedSuggestResponse,
  HierarchicalAggregationResponse,
  HierarchicalFacet,
  HierarchicalFacetClauseOneOf,
  IdentificationData,
  IdentificationDataIdOneOf,
  IndexDocument,
  InternalDocument,
  InternalDocumentUpdateByFilterOperation,
  InternalDocumentUpdateOperation,
  InternalUpdateExistingOperation,
  MaxAggregationResponse,
  MessageEnvelope,
  MinAggregationResponse,
  MinMaxAggregationResponse,
  NextPageResponse,
  OrderingClause,
  OrderingClauses,
  RelatedOptions,
  RelatedRequest,
  RelatedResponse,
  SearchOptions,
  SearchPaging,
  SearchProperty,
  SearchRequest,
  SearchResponse,
  SiteDocument,
  SuggestOptions,
  SuggestRequest,
  SuggestResponse,
  SumAggregationResponse,
  TermAggregationResponse,
  TermFacet,
  TrendingItems,
  TrendingOptions,
  TrendingRequest,
  TrendingResponse,
  UpdateByFilterOperation,
  UpdateDocumentsEvent,
  UpdateDocumentsEventOperationOneOf,
  UpdateExistingOperation,
  UpdateInternalDocumentsEvent,
  UpdateInternalDocumentsEventOperationOneOf,
  V1DeleteByFilterOperation,
  V1DeleteByIdsOperation,
  Value,
  VersionedDeleteByIdsOperation,
  VersionedDocumentId,
  VersionedDocumentUpdateOperation,
  VersioningMode,
  WebhookIdentityType,
} from './search-v1-sitedocument-site-search.universal.js';
